import { Modal } from "@nextui-org/react";
import React from "react";

/**
 * CustomModal component wraps the @nextui-org/react Modal component with additional customization.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal.
 * @param {boolean} props.isOpen - Controls whether the modal is open or closed.
 * @param {function} props.onOpenChange - Callback function to handle the open state change of the modal.
 * @param {string} [props.size=""] - Optional. Specifies the size of the modal. Default is an empty string.
 * @returns {JSX.Element} The CustomModal component.
 *
 * @example
 * 
 * const MyComponent = () => {
 *     const { isOpen, onOpen, onOpenChange } = useDisclosure();
 *
 *     return (
 *         <>
 *             <button onClick={onOpen}>Open Modal</button>
 *             <CustomModal isOpen={isOpen} onOpenChange={onOpenChange}>
 *                 <form method="POST" onSubmit={onSubmit}>
 *                     <ModalHeader className="flex flex-col gap-1 text-center text-primaryText">
 *                         <CategoryTitle className="!mb-2">Titre</CategoryTitle>
 *                     </ModalHeader>
 *                     <ModalBody className="items-center">
 *                         {children}
 *                     </ModalBody>
 *                     <ModalFooter className="justify-center">
 *                         <PrimaryButton className="mb-4">
 *                             Valider
 *                         </PrimaryButton>
 *                     </ModalFooter>
 *                 </form>
 *             </CustomModal>
 *         </>
 *     );
 * };
 * 
 * export default MyComponent;
 */

export function CustomModal({ children, isOpen, onOpenChange, size = "" }) {
    return (
        <Modal
            isDismissable={false}
            backdrop="opaque"
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            className="bg-mainBg text-primaryText"
            size={size}
            motionProps={{
                variants: {
                    enter: {
                        y: 0,
                        opacity: 1,
                        transition: {
                            duration: 0.3,
                            ease: "easeOut",
                        },
                    },
                    exit: {
                        y: -20,
                        opacity: 0,
                        transition: {
                            duration: 0.2,
                            ease: "easeIn",
                        },
                    },
                }
            }}
        >
            {children}
        </Modal>
    )
}
